import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Affiliate {
  id: string
  name: string
  email: string
  program_name: string
  status: string
  commission_rate: number
  total_revenue: number
  total_commission: number
  total_conversions: number
  conversion_rate: number
}

interface Leaderboard {
  rank: number
  affiliate_id: string
  name: string
  total_revenue: number
  total_commission: number
  conversions: number
}

export default function AffiliatesPage() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([])
  const [leaderboard, setLeaderboard] = useState<Leaderboard[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [affRes, leaderRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/affiliates/'),
      fetch(API_BASE + '/affiliates/leaderboard'),
      fetch(API_BASE + '/affiliates/statistics')
    ])
    if (affRes.ok) setAffiliates((await affRes.json()).affiliates || [])
    if (leaderRes.ok) setLeaderboard((await leaderRes.json()).leaderboard || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Affiliate Program</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Affiliate</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Affiliates</div>
              <div className="text-2xl font-bold">{statistics.total_affiliates}</div>
              <div className="text-xs text-green-600">{statistics.active_affiliates} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Revenue Generated</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue_generated)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Commission Paid</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_commission_paid)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Commission</div>
              <div className="text-2xl font-bold text-yellow-600">{formatCurrency(statistics.pending_commission)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Affiliates</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Program</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {affiliates.slice(0, 10).map(aff => (
                  <tr key={aff.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{aff.name}</div>
                      <div className="text-sm text-gray-500">{aff.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{aff.program_name}</div>
                      <div className="text-xs text-purple-600">{aff.commission_rate}% commission</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-green-600">{formatCurrency(aff.total_revenue)}</div>
                      <div className="text-xs text-gray-500">{aff.total_conversions} conversions</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(aff.status)}`}>{aff.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Leaderboard</h3>
            <div className="space-y-3">
              {leaderboard.map(item => (
                <div key={item.affiliate_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${item.rank === 1 ? 'bg-yellow-400 text-yellow-900' : item.rank === 2 ? 'bg-gray-300 text-gray-700' : item.rank === 3 ? 'bg-orange-300 text-orange-900' : 'bg-gray-100 text-gray-600'}`}>
                    {item.rank}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{item.name}</div>
                    <div className="text-xs text-gray-500">{item.conversions} sales</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">{formatCurrency(item.total_revenue)}</div>
                    <div className="text-xs text-purple-600">{formatCurrency(item.total_commission)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
